import React, { FC, useState } from "react";
import { LuChevronLeft, LuChevronRight, LuMail } from "react-icons/lu";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import { SettingsConfirmModal } from "../../../../components/modal/SettingsConfirmModal";
import { TrashSvg } from "../../../../components/svg/TrashSvg";
import { useAppSelector } from "../../../../redux/redux";
import { getLinkedFirm } from "../../../../sessionStorage/sessionStorage";
import {
  getSequenceErrorMessage,
  useDeleteSequenceTemplateMutation,
  useGetSequenceTemplatesQuery,
  useSaveSequenceTemplateMutation,
} from "../../../../slices/EmailSequencesSlice";
import { useGetEmailTemplatesQuery } from "../../../../slices/EmailTemplatesSlice";
import {
  EmailSequenceTemplate,
  SequenceForm,
} from "../../../work/pages/sequences/emailSequence.types";
import {
  buildTemplatePayload,
  formFromTemplate,
  getDefaultForm,
  STOP_SCOPE_TEXT,
} from "../../../work/pages/sequences/emailSequence.utils";
import { SequenceModal } from "../../../work/pages/sequences/SequenceModal";

export const EmailSequenceTemplates: FC = () => {
  const orgId = getLinkedFirm()?.orgId || "";
  const user = useAppSelector((state) => state.appReducer.user);
  const { data: templates = [], isFetching } = useGetSequenceTemplatesQuery(
    { orgId },
    { skip: !orgId },
  );
  const { data: emailTemplates = [] } = useGetEmailTemplatesQuery(
    { orgId },
    { skip: !orgId },
  );
  const [saveTemplate, { isLoading: isSaving }] =
    useSaveSequenceTemplateMutation();
  const [deleteTemplate, { isLoading: isDeleting }] =
    useDeleteSequenceTemplateMutation();

  const [editing, setEditing] = useState<EmailSequenceTemplate | "new" | null>(
    null,
  );
  const [toDelete, setToDelete] = useState<EmailSequenceTemplate | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async (form: SequenceForm) => {
    setError(null);
    try {
      await saveTemplate(
        buildTemplatePayload(form, {
          id: editing && editing !== "new" ? editing.id : undefined,
          orgId,
          userId: user?.id || "",
        }),
      ).unwrap();
      toast.success("Sequence template saved");
      setEditing(null);
    } catch (err) {
      setError(getSequenceErrorMessage(err, "Couldn't save the template."));
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await deleteTemplate({ id: toDelete.id }).unwrap();
      toast.success("Sequence template deleted");
      setToDelete(null);
    } catch {
      toast.error("Couldn't delete the template.");
    }
  };

  return (
    <div className={"mb-[120px] py-6 font-Manrope"}>
      <Link
        to={".."}
        relative={"path"}
        className={
          "mb-2 inline-flex items-center gap-0.5 text-[13.5px] font-semibold text-[#6B4EFF] hover:underline"
        }>
        <LuChevronLeft size={15} /> Templates
      </Link>
      <div className={"mb-6 flex items-center justify-between gap-4"}>
        <div>
          <div className={"text-[19px] font-bold text-[#1F2433]"}>
            Email sequence templates
          </div>
          <div className={"mt-1 text-[13.5px] text-[#5B6678]"}>
            Reusable multi-step email sequences your team can start in one
            click.
          </div>
        </div>
        <button
          type={"button"}
          onClick={() => {
            setError(null);
            setEditing("new");
          }}
          className={
            "h-9 rounded-lg bg-[#6B4EFF] px-4 text-[13.5px] font-bold text-white shadow-[0_6px_16px_rgba(107,78,255,0.25)] hover:bg-[#5A3EEB]"
          }>
          + New template
        </button>
      </div>

      {isFetching && !templates.length ? (
        <div className={"text-sm text-[#5F758F]"}>Loading templates…</div>
      ) : templates.length ? (
        <div className={"grid gap-3"}>
          {templates.map((template) => (
            <div
              key={template.id}
              role={"button"}
              tabIndex={0}
              onClick={() => {
                setError(null);
                setEditing(template);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") setEditing(template);
              }}
              className={
                "group flex cursor-pointer items-center gap-4 rounded-xl border border-[#E4E7EC] bg-white px-5 py-4 hover:border-[#B9AEFF]"
              }>
              <span
                className={
                  "flex h-10 w-10 items-center justify-center rounded-lg bg-[#F0EDFF] text-[#6B4EFF]"
                }>
                <LuMail size={17} />
              </span>
              <div className={"min-w-0 flex-1"}>
                <div className={"text-[14.5px] font-bold text-[#1F2433]"}>
                  {template.name}
                </div>
                <div className={"mt-0.5 text-[12.5px] text-[#8A90A2]"}>
                  {template.steps.length}{" "}
                  {template.steps.length === 1 ? "email" : "emails"} · stops on
                  reply in {STOP_SCOPE_TEXT[template.stopScope]}
                </div>
              </div>
              <button
                type={"button"}
                aria-label={`Delete ${template.name}`}
                onClick={(event) => {
                  event.stopPropagation();
                  setToDelete(template);
                }}
                className={
                  "rounded-md p-1.5 opacity-0 hover:bg-[#FDECEC] group-hover:opacity-100"
                }>
                <TrashSvg />
              </button>
              <LuChevronRight size={16} className={"text-[#B5BAC6]"} />
            </div>
          ))}
        </div>
      ) : (
        <div
          className={
            "rounded-xl border border-dashed border-[#DCE0E7] bg-white px-6 py-10 text-center text-[13.5px] text-[#8A90A2]"
          }>
          No sequence templates yet. Create one to start sequences in one click.
        </div>
      )}

      {editing && (
        <SequenceModal
          key={editing === "new" ? "new" : editing.id}
          mode={"template"}
          title={
            editing === "new"
              ? "New sequence template"
              : "Edit sequence template"
          }
          initialForm={
            editing === "new"
              ? getDefaultForm({ name: "", fromUserId: "", toEmail: "" })
              : formFromTemplate(editing)
          }
          emailTemplates={emailTemplates}
          isSaving={isSaving}
          error={error}
          onClose={() => setEditing(null)}
          onSubmit={handleSave}
        />
      )}

      {toDelete && (
        <SettingsConfirmModal
          onClose={() => setToDelete(null)}
          onConfirm={() => void handleDelete()}
          icon={<TrashSvg />}
          title={"Delete sequence template?"}
          description={`"${toDelete.name}" will be removed. Sequences already started from it keep running.`}
          confirmText={"Delete"}
          isLoading={isDeleting}
        />
      )}
    </div>
  );
};
